// 视频节目页
import * as React from 'react'

import Link from 'next/link'
import fetch from 'isomorphic-unfetch'
import ScrollLoader from 'Components/scroll-loader'

import getUrl from 'lib/url'
import formatTime from 'lib/format-time'
import formatDuration from 'lib/format-duration'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import get from 'lodash/get'

import Image from 'Components/image'
import Loading from 'Components/loading'
import Breadcrumb from 'Components/breadcrumb'
import FooterStatement from 'Components/footer-statement'

interface ArticleInfo {
  cover: string
  duration: number
  gid: number
  gid_str: string
  modifyTime: number
  source: string
  title: string
}

interface GroupItem {
  author_avatar: string
  author_link: string
  author_name: string
  column_id: number
  count: number
  cover: string
  has_followed_column: boolean
  logo: string
  media_id: number
  name: string
  user_id: number
  title: string
  createTime: number
  duaration: number
  gid: string
  hasVideo: boolean
  modifyTime: number
  publishTime: number
  source: string
  article: ArticleInfo
}

interface VideoGroupsProps {
  allGroups: GroupItem[]
  totalPage: number
}

function VideoGroups({ allGroups, totalPage }: VideoGroupsProps) {
  const [hasMore, setHasMore] = React.useState(true)
  const [loading, setLoading] = React.useState(false)
  const [listData, setListData] = React.useState<GroupItem[]>(allGroups)
  const pageNum = React.useRef(1)

  const loadMoreHandler = () => {
    if (listData.length >= totalPage) {
      return
    }

    pageNum.current += 1

    setLoading(true)
    fetchVideoGroup(pageNum.current)
      .then(res => res.json())
      .then(data => {
        setListData(listData.concat(get(data, 'data.list', [])))
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  return (
    <section>
      <ScrollLoader
        end={!hasMore}
        loading={loading}
        loadMoreHandler={loadMoreHandler}
      >
        <Breadcrumb
          path={[
            { name: '视频', href: '/video/home', as: '/video' },
            { name: '懂车帝原创' },
          ]}
        />
        <div className="group-list">
          {listData.map((item, index) => {
            return <GroupItem key={index} item={item} />
          })}
        </div>
      </ScrollLoader>
      {loading && <Loading />}
      <footer>
        <FooterStatement />
      </footer>
      <style jsx>
        {`
          section {
            width: 1190px;
            margin: 0 auto;
          }
          .group-list {
            display: flex;
            flex-wrap: wrap;
            margin-top: 20px;
          }
        `}
      </style>
    </section>
  )
}

interface GroupItemProps {
  item: GroupItem
}

function GroupItem({ item }: GroupItemProps) {
  const groupId = get(item, 'article_info[0].gid_str', '')
  const title = get(item, 'article_info[0].title', '')
  const duration = get(item, 'article_info[0].duration', 0)
  const modifyTime = get(item, 'article_info[0].publishTime', 0)
  const articleCover =
    get(item, 'article_info[0].cover', '') || get(item, 'cover', '')

  return (
    <Link
      href={`/video/group-origin?groupId=${item.column_id}`}
      as={`/video/groups-origin/${item.column_id}`}
      key={item.column_id}
    >
      <a
        target="_blank"
        className="group-item"
        onClick={() => {
          sendTeaCommEvent('click_column_cell', {
            column_id: item.column_id,
            column_name: item.name,
          })
        }}
      >
        <div>
          <div className="top">
            <div className="logo">
              <Image transition={false} src={item.logo} />
            </div>
            <div className="meta">
              <p className="group-name">{item.name}</p>
              <Link
                href={`/user/profile?mediaId=${item.media_id}`}
                as={`/user/profile/${item.media_id}`}
              >
                <a target="_blank">
                  <div className="author">
                    <p className="avatar">
                      <Image transition={false} src={item.author_avatar} />
                    </p>
                    <span className="name">{item.author_name}</span>
                  </div>
                </a>
              </Link>
            </div>
            <div className="count-wrap">
              <span className="count">共{item.count}期</span>
              <i className="more-ic icon-arrow-ic-r" />
            </div>
          </div>
          <Link href={`/article?gid=${groupId}`} as={`/article/${groupId}`}>
            <a target="_blank">
              <div className="bottom">
                <div className="left">
                  <p className="title">{title}</p>
                  <p className="time">{formatTime(modifyTime * 1000)}更新</p>
                </div>
                <div className="right">
                  <div className="video-cover">
                    <Image src={articleCover} />
                  </div>
                  <span className="duration">{formatDuration(duration)}</span>
                </div>
              </div>
            </a>
          </Link>
        </div>
        <style jsx>
          {`
            .group-item {
              width: 580px;
              margin: 0 30px 30px 0;
              border: 1px solid #e6e6e6;
              padding: 16px 20px;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              transition: box-shadow 0.2s ease-in-out;
            }
            .group-item:hover {
              box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
            }
            .group-item:nth-child(2n) {
              margin-right: 0;
            }
            .top {
              display: flex;
              align-items: center;
              border-bottom: 1px solid #e6e6e6;
              padding-bottom: 16px;
            }
            .bottom {
              display: flex;
              align-items: center;
              margin-top: 16px;
            }
            .meta {
              flex: 1;
            }
            .logo {
              width: 80px;
              height: 80px;
              margin-right: 20px;
            }
            .group-name {
              line-height: 28px;
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .author {
              display: flex;
              align-items: center;
            }
            .avatar {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              margin-right: 4px;
              border: 1px solid #e6e6e6;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .name {
              line-height: 22px;
              font-size: 16px;
              color: #999;
            }
            .time {
              line-height: 20px;
              font-size: 14px;
              color: #999;
            }
            .count {
              line-height: 22px;
              font-size: 16px;
            }
            .more-ic {
              content: '';
              display: inline-block;
              width: 8px;
              height: 16px;
              margin-top: 3px;
              margin-left: 4px;
              vertical-align: text-top;
              color: #000;
            }
            .title {
              line-height: 28px;
              font-size: 20px;
              font-weight: bold;
              margin: 0 20px 8px 0;
            }
            .right {
              position: relative;
            }
            .video-cover {
              width: 230px;
              height: 132px;
            }
            .duration {
              height: 20px;
              padding: 0 6px;
              line-height: 20px;
              position: absolute;
              right: 4px;
              bottom: 4px;
              font-size: 10px;
              color: #fff;
              border-radius: 10px;
              background-color: rgba(0, 0, 0, 0.4);
            }
          `}
        </style>
      </a>
    </Link>
  )
}

VideoGroups.getInitialProps = async () => {
  const data = await fetchVideoGroup()
    .then(res => res.json())
    .then(res => {
      console.log('res', res)
      return res
    })
    .catch(e => {
      console.log(e)
    })

  return {
    allGroups: get(data, 'data.list', []) || [],
    totalPage: get(data, 'data.total', 0) || 0,
  }
}

async function fetchVideoGroup(pageNumber = 1) {
  return fetch(
    getUrl(
      `/motor/pgc_column/api/m/v1/get_column_by_category/?${serializeObject({
        aid: 1839,
        category: '全部',
        sort_type: 'origin',
        count: 10,
        article_info_count: 1,
        page: pageNumber,
      })}`
    )
  )
}

export default VideoGroups
